import React from 'react';
import { Button as MuiButton, ButtonProps as MuiButtonProps, useTheme } from '@mui/material';
import { deepmerge } from '@mui/utils';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';

export type ButtonSize = 'small' | 'medium' | 'large' | 'xlarge';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'destructive'
  | 'link-primary'
  | 'link-secondary'
  | 'link-orange'
  | 'link-destructive';

export interface ButtonProps
  extends Omit<MuiButtonProps, 'variant' | 'size' | keyof PounceStyleProps>, PounceStyleProps {
  /** The size (height) of the button */
  size?: ButtonSize;

  /** The variant of the button that decides the colors, defaults to primary */
  variant?: ButtonVariant;

  /** Whether the button should occupy entirety of its parent. Defaults to `false` */
  fullWidth?: boolean;

  /** Whether the button is disabled */
  disabled?: boolean;

  /** Whether the button should have a loading spinner next to it */
  loading?: boolean;

  /** Whether the button should always be marked as active. Helpful for menus. */
  active?: boolean;

  /** Additional styles to apply (combined with pounce props) */
  sx?: MuiButtonProps['sx'];
}

/**
 * The core re-usable Button component built on top of MUI Button.
 *
 * Provides all the variants and functionality from the original Pounce Button
 * while leveraging MUI's accessibility and interaction patterns.
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    size = 'large',
    variant = 'primary',
    loading = false,
    fullWidth = false,
    active = false,
    disabled = false,
    children,
    sx,
    ...rest
  },
  ref,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);
  const isDisabled = disabled;
  const muiTheme = useTheme();

  // Build loading styles if needed
  // Use '&&' to double selector specificity so the gradient overrides the theme variant's
  // backgroundColor class (same 0,1,0 specificity, last-wins could go either way).
  // @keyframes stays at the top level — it cannot be nested inside a selector.
  const loadingStyles = loading
    ? {
        '&&': {
          background: `linear-gradient(to right, ${muiTheme.palette.componentTokens?.buttons.colors.coreIcon[variant]?.background.hover} 50%, ${muiTheme.palette.componentTokens?.buttons.colors.coreIcon[variant]?.background.resting} 50%)`,
          backgroundSize: '200% 100%',
          animation: 'pounce-loading 4s linear infinite reverse',
        },
        '@keyframes pounce-loading': {
          '0%': {
            backgroundPosition: '-200% 0',
          },
          '100%': {
            backgroundPosition: '200% 0',
          },
        },
      }
    : {};

  // Merge styles with proper priority using MUI's deepmerge:
  // 1. Loading styles (base)
  // 2. Pounce styles (override loading and variant styles - since they're in sx prop, they override theme variants)
  // 3. User sx (highest priority - overrides everything)
  const combinedSx: MuiButtonProps['sx'] = deepmerge(
    deepmerge(loadingStyles, pounceStyles),
    sx || {},
  ) as MuiButtonProps['sx'];

  return (
    <MuiButton
      ref={ref}
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      disabled={isDisabled}
      aria-pressed={active}
      data-active={active || undefined}
      sx={combinedSx}
      {...otherProps}
    >
      {children}
    </MuiButton>
  );
});
